import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { assignMemberRole, listOrgRoles } from '../../api/resources';
import type { OrganizationMember } from '../../types/api';

function memberRoleId(member: OrganizationMember): string | null {
    return member.memberRoles?.[0]?.roleId ?? member.roleId ?? null;
}

/**
 * Set the member's single organization role. Assigning a new role replaces any
 * previously assigned role.
 */
export function MemberRolesSection({ member }: { member: OrganizationMember }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const rolesQuery = useQuery({
        queryKey: ['org-roles', member.organizationId],
        queryFn: () => listOrgRoles(member.organizationId),
    });

    const options = useMemo(
        () =>
            (rolesQuery.data?.roles ?? []).map((role) => ({
                value: role.id,
                label: role.displayName,
            })),
        [rolesQuery.data],
    );

    const assignMutation = useMutation({
        mutationFn: (roleId: string) => assignMemberRole(member.id, roleId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['organization-members'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const currentRoleId = memberRoleId(member);

    return (
        <Stack gap="xs">
            <Text size="xs" tt="uppercase" fw={600} c="dimmed">
                {t('detail.memberRole')}
            </Text>
            <Text size="xs" c="dimmed">
                {t('detail.memberRoleHint')}
            </Text>
            <Select
                placeholder={t('detail.assignRolePlaceholder')}
                data={options}
                value={currentRoleId}
                onChange={(roleId) => {
                    if (roleId && roleId !== currentRoleId) {
                        assignMutation.mutate(roleId);
                    }
                }}
                searchable
                allowDeselect={false}
                disabled={assignMutation.isPending || rolesQuery.isLoading}
            />
        </Stack>
    );
}
